import React, { useEffect, useRef, useState } from 'react'
import Icon from 'components/elements/Icon'
import { DragDropContext, Droppable, Draggable, DropResult } from 'react-beautiful-dnd'

import { DeckTagRelation, DeckTagRelationListResponse } from 'services/apiTypes/deckTag.types'

import { useActionless, useAppSelector } from 'redux/hooks'
import { SET_DECK_STATE } from 'redux/deck/actions/types'

import DeckTagService from 'services/deckTag.service'
import ToastService from 'services/toast.service'

import Link from 'components/elements/Link'
import LineLoader from 'components/elements/LineLoader'
import DeckTagSelector from 'components/formElements/DeckTagSelector'

import styles from './deckTagSettings.module.scss'

type Props = {}

const DeckTagSettings = ({}: Props) => {
  const [tags, setTags] = useState<DeckTagRelationListResponse>({ count: 0, next: null, previous: null, results: [] })
  const [loading, setLoading] = useState(true)

  const deckId = useAppSelector(state => state.deck.id)

  const [setDeckState] = useActionless(SET_DECK_STATE)

  const handleAddTag = (tagId: any) => {
    const newLastPosition = getNewPosition(null, tags.results.length, tags.results)

    DeckTagService.createDeckRelation(deckId, tagId, newLastPosition).then(() => {
      handleFetchDeckTags()
    })
  }

  const handleFetchDeckTags = () => {
    DeckTagService.getTags(deckId)
      .then(res => {
        setTags(res)
        setDeckState({ deckTags: res.results })
      })
      .catch(() => ToastService.create('Unable to fetch deck tags'))
      .finally(() => setLoading(false))
  }

  const handleDeleteTag = (tagId?: number | string) => {
    if (!tagId) return

    DeckTagService.removeDeckRelation(tagId)
      .then(() => handleFetchDeckTags())
      .catch(() => ToastService.create('Unable to delete tag'))
  }

  const handleReorderItems = (result: DropResult) => {
    if (!result.destination) return

    const reorder = (list: any[], startIndex: number, endIndex: number) => {
      const result = Array.from(list)
      const [removed] = result.splice(startIndex, 1)
      result.splice(endIndex, 0, removed)

      return result
    }

    const reorderedTags = reorder(tags.results, result.source.index, result.destination.index)

    setTags({ ...tags, results: reorderedTags })

    const newPosition = getNewPosition(result.source.index, result.destination.index, tags.results)
    const item = tags.results[result.source.index]

    DeckTagService.updateDeckRelation({ ...item, position: newPosition }).then(handleFetchDeckTags)
  }

  useEffect(handleFetchDeckTags, [])

  const alreadySelectedTags = tags.results.reduce(
    (acc, tag) => ({ ...acc, [tag.name || '']: true }),
    {} as Record<string, boolean>,
  )

  return (
    <div className={styles.container}>
      <p className={styles.blurb}>
        Deck tags are created and managed by the Archidekt team so they can be made to be searchable. If you can't find
        a tag that you feel should be present, feel free to reach out either on{' '}
        <a href="https://discord.gg/GyAAANU8uJ" target="_blank" rel="noopener noreferrer">
          discord
        </a>{' '}
        or our{' '}
        <Link href="/forum/General/Development" target="_blank" rel="noopener noreferrer">
          forums
        </Link>
        .
      </p>

      <DeckTagSelector
        clearValueOnSelection
        className={styles.selector}
        disabled={tags.count > 9}
        onSelect={handleAddTag}
        disabledOptions={alreadySelectedTags}
      />

      {loading && <LineLoader />}
      {!loading && !tags.results.length && <div>No tags</div>}
      {!loading && !!tags.results.length && (
        <DragDropContext onDragEnd={handleReorderItems}>
          <Droppable droppableId="deck-tags">
            {dropContext => (
              <div {...dropContext.droppableProps} ref={dropContext.innerRef} className={styles.dropContainer}>
                {tags.results.map((tag, index) => (
                  <Draggable draggableId={`${tag.id}`} index={index} key={tag.id}>
                    {(dragContext, snapshot) => (
                      <div
                        ref={dragContext.innerRef}
                        className={styles.row}
                        key={tag.id}
                        {...dragContext.dragHandleProps}
                        {...dragContext.draggableProps}>
                        <span>
                          <div className={styles.dragHandle}>
                            <Icon name="bars" />
                          </div>
                          <label>{tag.name}</label>
                        </span>
                        {!snapshot.isDragging && (
                          <button className={styles.removeButton} onClick={() => handleDeleteTag(tag.id)}>
                            <Icon name="trash alternate outline" />
                          </button>
                        )}
                      </div>
                    )}
                  </Draggable>
                ))}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      )}
    </div>
  )
}

export default DeckTagSettings

type TokenizedPosition = {
  letter: string
  number: number
}

const getNewPosition = (
  sourceIndex: number | null,
  destinationIndex: number,
  existingArray: DeckTagRelation[],
): string => {
  // Starting point
  if (sourceIndex === null && !existingArray.length) return 'M-500000'

  // If there is no source index, we are adding a new item to the end of the list, get the next position after the Nth index
  // Or if we're just shoving the item onto the end of the array
  if ((sourceIndex === null && existingArray.length) || destinationIndex === existingArray.length - 1) {
    const lastPosition = existingArray[existingArray.length - 1].position
    const tokenizedPosition = getTokenizedPosition(lastPosition)

    return getAdjacentPosition(tokenizedPosition)
  }

  if (destinationIndex === 0) {
    const firstPosition = existingArray[0].position
    const tokenizedPosition = getTokenizedPosition(firstPosition)

    return getAdjacentPosition(tokenizedPosition, true)
  }

  const previousPosition = existingArray[destinationIndex - 1].position
  const nextPosition = existingArray[destinationIndex].position

  const halfWayPosition = getHalfWayPosition(previousPosition, nextPosition)

  return halfWayPosition
}

const offsetAmmount = 10000

const getHalfWayPosition = (previousPosition: string, nextPosition: string): string => {
  const previous = getTokenizedPosition(previousPosition)
  const next = getTokenizedPosition(nextPosition)

  const diff = next.number > previous.number ? next.number - previous.number : offsetAmmount - previous.number

  return tokenToString({
    letter: previous.letter,
    number: Math.floor(previous.number + diff / 2),
  })
}

const getAdjacentPosition = (tokenizedPosition: TokenizedPosition, previous = false): string => {
  const { letter, number } = tokenizedPosition

  if (number + offsetAmmount < 1000000 && !previous)
    return tokenToString({ ...tokenizedPosition, number: number + offsetAmmount })
  if (number - offsetAmmount > 0 && previous)
    return tokenToString({ ...tokenizedPosition, number: number - offsetAmmount })

  if (number + offsetAmmount > 999999 && !previous && letter !== 'Z')
    return tokenToString({ letter: adjacentLetter(letter), number: 0 })
  if (number - offsetAmmount < 1 && previous && letter !== 'A')
    return tokenToString({ letter: adjacentLetter(letter, true), number: 900000 })

  if (number + offsetAmmount > 999999 && !previous && letter === 'Z')
    return tokenToString({ letter: adjacentLetter(letter), number: 0 })
  if (number - offsetAmmount < 1 && previous && letter === 'A')
    return tokenToString({ letter: adjacentLetter(letter, true), number: 9000 })

  return previous ? 'A-000000' : 'Z-999999' // This code path should ideally never be reached
}

const tokenToString = (tokenizedPosition: TokenizedPosition): string =>
  `${tokenizedPosition.letter}-${tokenizedPosition.number}`

const getTokenizedPosition = (position: string): TokenizedPosition => {
  const [first, second] = position.split('-')

  return {
    letter: first[0] ?? 'M',
    number: Number(second) ?? 500000,
  }
}

const adjacentLetter = (letter: string, previous?: boolean) => {
  const char = letter.toLocaleUpperCase()

  if (char === '0' && previous) return '0'
  if (char === '9' && !previous) return 'A'
  if (char === 'A' && previous) return '9'
  if (char === 'Z' && !previous) return 'Z'

  return String.fromCharCode(char.charCodeAt(0) + (previous ? -1 : 1))
}
